import { VuexModule, Module, Mutation, Action } from "vuex-module-decorators";
import { RootState } from "@/store";
import { Toast } from "vant";
import { cloneDeep } from "lodash-es";
import { FORMAT_DATE_SMALL, isFinish } from "@/utils/common/date";
import { isBegin } from "@/utils/common/date";
import { handlePaginationData, queryRequest } from "@/utils/request";
import {
  queryK2PriceActivity,
  searchK2ActivitySku
} from "@/graphql/promotion.gql";
import { GTGroupActivityParam, GTGroupFixedOrgParam } from "@/enums/app";
import { getAttributes, getAttrName } from "@/utils/mall/mall";
import {
  getCartSkuAmount,
  calculateCart,
  addCart,
  updateCartSku,
  deleteCartSku,
  updateCartSkuList,
  deleteCartBySelected,
  wtCheckOutCart,
  clearCartOffSale,
  clearCart
} from "@/graphql/cart.gql";
import { skuIsExpress } from "@/graphql/product.gql";

// 使用旧的价格，图片
const syncOldSkuData = (newData = [], oldData = []) => {
  if (newData.length) {
    const skuMap = Object.create(null);
    oldData.forEach(sku => (skuMap[sku.skuCode] = sku));
    newData.forEach(cartSku => {
      cartSku.productPrice = skuMap[cartSku.skuCode].productPrice;
      cartSku.mediaLink = skuMap[cartSku.skuCode].mediaLink;
      cartSku.mobileMediaLink = skuMap[cartSku.skuCode].mobileMediaLink;
    });
  }
  return newData.slice();
};

// 使用新的数量
const syncNewSkuData = (newData = [], oldData = []) => {
  if (oldData.length) {
    const skuMap = Object.create(null);
    newData.forEach(sku => (skuMap[sku.skuCode] = sku));
    oldData.forEach(cartSku => {
      cartSku.count = skuMap[cartSku.skuCode].count;
    });
  }
  return oldData.slice();
};

export interface IPromotionState {
  promotion: any;
  promotionProducts: any[];
  cartData: any[];
  activityCode: string;
  totalPrice: string;
  showCartPopup: boolean;
  addingCart: boolean;
  skuExpressMap;
}

@Module({ name: "PromotionDetailModule", namespaced: true })
export default class PromotionDetailModule
  extends VuexModule<IPromotionState, RootState>
  implements IPromotionState {
  // 活动
  public promotion = {};
  // 活动商品
  public promotionProducts = [];
  // 购物车商品
  public cartData = [];
  // 活动编号
  public activityCode: string = "";
  // 购物车总价
  public totalPrice: string = "";
  // 购物车弹窗
  public showCartPopup: boolean = false;
  // 添加购物车途中
  public addingCart: boolean = false;
  // sku是否支持快递映射表
  skuExpressMap: any = {};

  // 购物车原价的总价
  get originPriceTotal() {
    return parseFloat(
      this.cartData.reduce((t, o) => t + o.productPrice * (o.count || 1), 0)
    ).toFixed(2);
  }
  @Mutation
  clearSkuExpressMapMutation() {
    this.skuExpressMap = {};
  }
  @Mutation
  saveSkuExpressMapMutation(data = {}) {
    this.skuExpressMap = Object.assign({}, this.skuExpressMap, data);
  }
  @Mutation
  saveAddingCartMutation(data: boolean) {
    this.addingCart = data || false;
  }

  @Mutation
  saveActivityCodeMutation(data: string = "") {
    this.activityCode = data || "";
  }
  @Mutation
  saveCartPopupMutation(data: boolean = false) {
    this.showCartPopup = data;
  }
  @Mutation
  saveTotalPriceMutation(data: string = "") {
    this.totalPrice = data || "";
  }
  @Mutation
  savePromotionMutation(data = {}) {
    this.promotion = data;
  }
  @Mutation
  savePromotionProductsMutation(data = []) {
    this.promotionProducts = data || [];
  }

  @Mutation
  setCartDataMutation(data = []) {
    this.cartData = data || [];
  }

  @Mutation
  addCartDataMutation(data = {}) {
    this.cartData.push(data);
  }
  @Mutation
  updateCartDataMutation(data = {}) {
    this.cartData.splice(
      this.cartData.findIndex(o => o.skuCode === (<any>data).skuCode),
      1,
      data
    );
  }
  @Mutation
  deleteCartDataMutation(data: any = {}) {
    this.cartData.splice(
      this.cartData.findIndex(o => o.skuCode === data.skuCode),
      1
    );
  }
  @Mutation
  clearCartDataMutation() {
    this.cartData = [];
  }

  // 清空页面数据
  @Action
  clearPageCacheDataAction() {
    const { commit } = this.context;
    commit("savePromotionMutation");
    commit("savePromotionProductsMutation");
    commit("saveCartPopupMutation");
    commit("setCartDataMutation");
    commit("saveActivityCodeMutation");
  }

  // 查询页面数据
  @Action
  async initDataAction() {
    const { state, commit } = this.context;
    const { data } = handlePaginationData(
      await queryRequest({
        query: queryK2PriceActivity,
        variables: {
          input: { activityCode: state.activityCode },
          pageInput: { pageNum: 1 }
        }
      })
    );
    commit("savePromotionMutation", data[0]);
  }

  @Action
  async queryK2ActivitySkuAction() {
    const { state, commit, dispatch } = this.context;
    const data = await queryRequest({
      query: searchK2ActivitySku,
      variables: { priceActivityCode: state.activityCode }
    });
    commit("clearSkuExpressMapMutation");
    await dispatch("querySkuHasExpress", data);
    commit("savePromotionProductsMutation", data);
    commit("savePromotionProductsMutation", data);
  }
  // 查询sku是否支持快递
  @Action
  async querySkuHasExpress(dataList) {
    const { list = [] } = await this.context.dispatch(
      "getSkuHasExpressAction",
      dataList
        .filter(o => o.skuList?.length)
        .map(item => {
          const attr = getAttributes(item.attributes);
          return {
            skuCode: item.skuList[0].skuCode,
            pattern: attr._pattern,
            specifications: attr._specification
          };
        })
    );

    this.context.commit(
      "saveSkuExpressMapMutation",
      Object.assign({}, ...list.map(o => ({ [o.skuCode]: o.isExpress })))
    );
    return dataList;
  }
  @Action
  async getSkuHasExpressAction(input = []) {
    return await queryRequest({
      query: skuIsExpress,
      variables: {
        input
      }
    });
  }

  // 添加购物车
  @Action
  async addCartAction(sku) {
    const {
      state: { cartData },
      dispatch
    } = this.context;

    // 如果存在购物车，数量就 +1
    let existSku;
    if ((existSku = cloneDeep(cartData.find(o => o.skuCode === sku.skuCode)))) {
      existSku.count++ || (existSku.count = 1);
      await dispatch("updateCartItemAction", existSku);
    }
    // 添加购物车
    else {
      // 如果购物车没有商品，就清除所有，清除其他活动之前添加的商品
      if (!cartData.length) {
        await dispatch("clearAllCartAction");
      }
      sku.count = 1;
      await dispatch("addCartItemAction", cloneDeep(sku));
    }
  }

  // 添加购物车商品请求
  @Action
  async addCartItemAction(sku) {
    const {
      state: { promotion }
    } = this.context;

    const { success } = await queryRequest({
      query: addCart,
      variables: {
        input: {
          skuCode: sku.skuCode,
          count: sku.count,
          priceActivityCode: promotion.activityCode,
          ...GTGroupActivityParam,
          ...GTGroupFixedOrgParam
        }
      }
    });
    // 添加购物车需要更新购物车数量
    // this.context.dispatch("getCartSkuAmountAction");
    this.context.commit("addCartDataMutation", sku);
    this.context.dispatch("calculatePriceAction");
    return success;
  }

  // 修改购物车商品数量
  @Action
  async updateCartItemAction(sku) {
    const {
      state: { cartData }
    } = this.context;
    const { success } = await queryRequest({
      query: updateCartSku,
      variables: {
        input: {
          skuCode: sku.skuCode,
          count: sku.count,
          ...GTGroupFixedOrgParam,
          ...GTGroupActivityParam
        }
      }
    });
    // 更新数量
    const exist = cartData.find(o => o.skuCode === sku.skuCode);
    this.context.commit("updateCartDataMutation", {
      ...exist,
      count: sku.count
    });
    this.context.dispatch("calculatePriceAction");
    // this.context.dispatch("getCartSkuAmountAction");
    return success;
  }

  // 删除购物车商品
  @Action
  async deleteCartItemAction(sku) {
    const { commit, dispatch } = this.context;
    const success = await queryRequest({
      query: deleteCartSku,
      variables: {
        input: {
          skuCode: sku.skuCode,
          ...GTGroupFixedOrgParam,
          ...GTGroupActivityParam
        }
      }
    });
    if (success) {
      commit("deleteCartDataMutation", sku);
      // dispatch("calculatePriceAction");
      dispatch("closeCartPopupAction");
    }
  }

  // 清空购物车(废弃)
  @Action
  async clearCartAction() {
    const {
      commit,
      dispatch,
      state: { cartData }
    } = this.context;
    // 清空当前购物车
    for (let i = 0; i < cartData.length; i++) {
      // await dispatch("deleteCartItemAction", cartData[i]);
    }

    commit("clearCartDataMutation");
    dispatch("calculatePriceAction");
    dispatch("closeCartPopupAction");
  }

  // 清空购物车所有
  @Action
  async clearAllCartAction() {
    // 清空所有
    const { commit, dispatch } = this.context;

    const success = queryRequest({
      query: clearCart,
      variables: {
        ...GTGroupActivityParam
      }
    });
    commit("clearCartDataMutation");
    dispatch("calculatePriceAction");
    dispatch("closeCartPopupAction");
    return success;
  }

  @Action
  closeCartPopupAction() {
    // 购物车没有了商品之后，关闭弹窗
    const {
      state: { cartData },
      commit
    } = this.context;
    if (!cartData.length) commit("saveCartPopupMutation");
  }

  // 购物车列表
  @Action
  async queryCartListAction() {
    const {
      commit,
      state: { activityCode, promotionProducts }
    } = this.context;
    const { actualPrice, checkOutStoreList = [] } = await queryRequest({
      query: calculateCart,
      variables: {
        input: {
          ...GTGroupActivityParam,
          priceActivityCode: activityCode
        }
      }
    });
    // 使用旧的价格
    const newSkuList = syncOldSkuData(
      checkOutStoreList[0]?.checkOutProductList,
      promotionProducts
    );

    commit("setCartDataMutation", newSkuList);
    commit("saveTotalPriceMutation", actualPrice);
    // commit("saveCartPriceMapMutation", {
    //   actualPrice,
    //   productAmount,
    //   discount
    // });
  }

  // 计算价格
  @Action
  async calculatePriceAction() {
    const {
      commit,
      state: { cartData = [], activityCode, promotionProducts }
    } = this.context;
    const {
      actualPrice,
      discount,
      productAmount,
      checkOutStoreList = []
    } = await queryRequest({
      query: calculateCart,
      variables: {
        input: {
          storeInputs: [
            {
              productInputList: cartData.map(({ skuCode }) => ({
                selectFlag: "01",
                skuCode,
                ...GTGroupFixedOrgParam
              }))
            }
          ],
          ...GTGroupActivityParam,
          priceActivityCode: activityCode
        }
      }
    });
    console.log(checkOutStoreList[0]?.checkOutProductList, promotionProducts);
    // 使用新的数量
    const oldSkuList = syncNewSkuData(
      checkOutStoreList[0]?.checkOutProductList,
      cartData
    );

    commit("setCartDataMutation", oldSkuList);
    commit("saveTotalPriceMutation", actualPrice);
    // commit("saveCartPriceMapMutation", {
    //   actualPrice,
    //   productAmount,
    //   discount
    // });
  }

  // 验证活动是否开始
  @Action
  verifyBeginAction() {
    const {
      state: { promotion }
    } = this.context;
    if (!isBegin(promotion.countDownStart, FORMAT_DATE_SMALL)) {
      Toast("活动还未开始，请耐心等待");
      return false;
    }
    if (isFinish(promotion.countDownEnd, FORMAT_DATE_SMALL)) {
      Toast("活动已结束，感谢参与");
      return false;
    }
    return true;
  }

  // 验证是否超出限购 （秒杀，弃用，由后端处理）
  @Action
  verifyLimitAction(sku) {
    const {
      state: { cartData }
    } = this.context;
    let existSku = cartData.find(o => o.skuCode === sku.skuCode);
    if (existSku && (existSku.count || 0) + 1 > existSku.maxPerUser) {
      Toast(`每人限购${sku.maxPerUser}件`);
      return false;
    }
    return true;
  }

  // 结算
  @Action
  async settleAction() {
    // const {
    //   state: { activityCode, cartData }
    // } = this.context;
    // // console.log(flashSaleCartSkus, amount);
    // return queryRequest({
    //   query: flashSaleCheckOut,
    //   variables: {
    //     input: cartData.map(
    //       ({ skuCode, skuName, imageUrl, flashPrice, salePrice, count }) => ({
    //         activityCode,
    //         skuCode,
    //         skuName,
    //         imageUrl,
    //         flashPrice,
    //         salePrice,
    //         count
    //       })
    //     )
    //   }
    // });

    const {
      state: { cartData, promotion }
    } = this.context;
    let data = await queryRequest({
      query: wtCheckOutCart,
      variables: {
        input: {
          storeInputs: [
            {
              productInputList: cartData.map(({ skuCode, count }) => ({
                skuCode,
                isExpress: 0, // 否
                selectFlag: "01",
                count,
                ...GTGroupFixedOrgParam,
                priceActivityCode: promotion.activityCode
              }))
            }
          ],
          ...GTGroupActivityParam
        }
      }
    });

    const {
      checkOutStoreList: [storeInfo]
    } = data;
    const { amount, checkOutProductList: flashSaleCartSkus } = storeInfo;
    return { amount, flashSaleCartSkus, storeInfo };
  }
}
